import { Check, ArrowRight } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { PageHero, CTABand, SectionTitle } from "@/components/blocks";
import { IMAGES } from "@/data/content";
import { useUI } from "@/context/UIContext";

const CURVE = [
  { rpm: 1500, origine: 90, stage: 130 },
  { rpm: 2000, origine: 150, stage: 205 },
  { rpm: 2500, origine: 195, stage: 250 },
  { rpm: 3000, origine: 210, stage: 268 },
  { rpm: 3500, origine: 215, stage: 272 },
  { rpm: 4000, origine: 205, stage: 265 },
  { rpm: 4500, origine: 190, stage: 250 },
  { rpm: 5000, origine: 170, stage: 232 },
];

const STEPS = [
  "Mesure de puissance et de couple à la roue",
  "Contrôle des paramètres moteur avant intervention",
  "Développement de la cartographie sur mesure",
  "Analyse des données et des logs moteur",
  "Validation de la cartographie au banc",
  "Contrôle après optimisation et remise du rapport",
];

export default function Banc() {
  const { openLead } = useUI();
  return (
    <div data-testid="banc-page">
      <Seo title="Banc de puissance | Mesure & validation — Horsepower Garage"
        description="Banc de puissance Horsepower : mesure de puissance et de couple, contrôle avant/après, analyse des données et validation de chaque cartographie moteur."
        canonical="https://www.horsepowergarage.fr/services/banc-de-puissance" />
      <PageHero eyebrow="Banc de puissance" title="La performance, mesurée et prouvée"
        subtitle="Chaque cartographie Horsepower est développée et validée au banc de puissance. Mesure réelle, données précises, résultats prouvés."
        image={IMAGES.sportsDark} tall />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <SectionTitle eyebrow="Courbe de puissance" title="Avant / après en un coup d'œil"
          sub="Exemple de courbe de couple (Nm) mesurée au banc : origine vs Stage 1." />
        <Reveal>
          <div className="hp-card p-4 sm:p-8 mt-10" data-testid="banc-chart">
            <ResponsiveContainer width="100%" height={360}>
              <LineChart data={CURVE} margin={{ top: 10, right: 20, bottom: 10, left: -10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                <XAxis dataKey="rpm" stroke="#888" tick={{ fill: "#888", fontSize: 12 }} label={{ value: "Régime (tr/min)", position: "insideBottom", offset: -4, fill: "#888", fontSize: 12 }} />
                <YAxis stroke="#888" tick={{ fill: "#888", fontSize: 12 }} />
                <Tooltip contentStyle={{ background: "#111", border: "1px solid #333", color: "#fff" }} />
                <Legend wrapperStyle={{ color: "#fff" }} />
                <Line type="monotone" dataKey="origine" name="Origine" stroke="#888" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="stage" name="Stage 1" stroke="#FFED00" strokeWidth={3} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <SectionTitle eyebrow="Notre méthodologie" title="Une démarche technique rigoureuse" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
          {STEPS.map((s, i) => (
            <Reveal key={s} delay={(i % 3) * 0.08}>
              <div className="hp-card p-6 h-full flex gap-4">
                <span className="font-stat font-black text-4xl text-hp-yellow leading-none">{String(i + 1).padStart(2, "0")}</span>
                <p className="text-neutral-200 self-center">{s}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <div className="mt-10 flex justify-center">
          <button onClick={() => openLead({ request_type: "rdv", service: "Banc de puissance" })} className="hp-cta px-8 py-4 text-sm inline-flex items-center gap-2" data-testid="banc-cta">
            Réserver un passage au banc <ArrowRight size={16} />
          </button>
        </div>
      </section>
      <CTABand />
    </div>
  );
}
